import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, endpoints } from '@/api/client';
import { queryKeys } from '@/api/queryClient';
import type { TaskListResponse, TaskDetail } from '@/types';

interface TaskFilters {
  [key: string]: string | number | undefined;
  page?: number;
  limit?: number;
  status?: string;
  type?: string;
  sort?: string;
  order?: 'asc' | 'desc';
}

/**
 * Hook to fetch paginated task list
 */
export function useTasks(filters: TaskFilters = {}) {
  return useQuery({
    queryKey: queryKeys.tasks.list(filters),
    queryFn: async () => {
      const response = await api.get<TaskListResponse>(endpoints.tasks.list, {
        params: {
          page: filters.page || 1,
          limit: filters.limit || 25,
          status: filters.status,
          type: filters.type,
          sort: filters.sort || 'createdAt',
          order: filters.order || 'desc',
        },
      });
      return response;
    },
    // Poll for updates every 30 seconds when there are active tasks
    refetchInterval: (query) => {
      const hasActiveTasks = query.state.data?.tasks?.some(
        (t) => ['queued', 'scheduled', 'executing', 'verifying'].includes(t.status)
      );
      return hasActiveTasks ? 30000 : false;
    },
  });
}

/**
 * Hook to fetch single task details
 */
export function useTask(id: string | null) {
  return useQuery({
    queryKey: queryKeys.tasks.detail(id || ''),
    queryFn: async () => {
      if (!id) throw new Error('Task ID required');
      return api.get<TaskDetail>(endpoints.tasks.get(id));
    },
    enabled: !!id,
    // Poll active tasks more frequently
    refetchInterval: (query) => {
      const task = query.state.data;
      const isActive = task && ['executing', 'verifying'].includes(task.status);
      return isActive ? 10000 : false;
    },
  });
}

/**
 * Hook to cancel a task
 */
export function useCancelTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (taskId: string) => {
      return api.post(`${endpoints.tasks.get(taskId)}/cancel`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.all });
    },
  });
}

/**
 * Hook to retry a failed task
 */
export function useRetryTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (taskId: string) => {
      return api.post(`${endpoints.tasks.get(taskId)}/retry`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.all });
    },
  });
}
